import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImageUploader from './components/ImageUploader';
import {saveCaseStudy,getAllCaseStudies,deleteCaseStudy} from '../utils/caseStudyStorage';
import {getAdminConfig,saveAdminConfig} from '../utils/adminConfig';

const {FiPlus,FiEdit2,FiTrash2,FiExternalLink,FiSave,FiX,FiSettings,FiImage,FiVideo,FiLink,FiGrid,FiList,FiSearch,FiFilter,FiStar,FiCalendar,FiUser,FiClock,FiTag,FiFolderOpen}=FiIcons;

const CaseStudyManager=()=> {
  const [caseStudies,setCaseStudies]=useState([]);
  const [isEditing,setIsEditing]=useState(false);
  const [editingStudy,setEditingStudy]=useState(null);
  const [showConfig,setShowConfig]=useState(false);
  const [viewMode,setViewMode]=useState('grid');
  const [searchQuery,setSearchQuery]=useState('');
  const [filterIndustry,setFilterIndustry]=useState('all');
  const [isLoading,setIsLoading]=useState(true);
  const [isDeleting,setIsDeleting]=useState(null); // Track which item is being deleted

  const [config,setConfig]=useState({
    industries: [
      'FinTech',
      'E-commerce',
      'Healthcare',
      'Education Technology',
      'SaaS',
      'Manufacturing',
      'Real Estate',
      'Entertainment'
    ],
    projectTypes: [
      'Web Application',
      'Mobile App',
      'API Development',
      'AI Integration',
      'System Migration',
      'Performance Optimization',
      'Security Audit',
      'Custom Software'
    ],
    technologies: [
      'React',
      'Node.js',
      'Python',
      'TypeScript',
      'Next.js',
      'Vue.js',
      'Express',
      'Django',
      'PostgreSQL',
      'MongoDB',
      'Redis',
      'AWS',
      'Docker',
      'Kubernetes',
      'GraphQL',
      'REST API',
      'Machine Learning',
      'AI/ML',
      'TensorFlow',
      'OpenAI'
    ]
  });

  const [formData,setFormData]=useState({
    title: '',
    client: '',
    industry: '',
    projectType: '',
    challenge: '',
    solution: '',
    results: [{metric: '',value: ''}],
    testimonial: '',
    author: '',
    image: '',
    gallery: [],
    projectUrl: '',
    demoUrl: '',
    technologies: [],
    duration: '',
    teamSize: '',
    featured: false,
    status: 'active'
  });

  useEffect(()=> {
    loadCaseStudies();
    loadConfig();
  },[]);

  // Listen for new case study creation events
  useEffect(()=> {
    const handleCreateNewCaseStudy=()=> {
      setIsEditing(true);
      resetForm();
    };
    
    window.addEventListener('createNewCaseStudy',handleCreateNewCaseStudy);
    return ()=> window.removeEventListener('createNewCaseStudy',handleCreateNewCaseStudy);
  },[]);

  const loadCaseStudies=async ()=> {
    try {
      setIsLoading(true);
      const allStudies=await getAllCaseStudies();
      console.log('Loaded case studies:',allStudies);
      setCaseStudies(Array.isArray(allStudies) ? allStudies : []);
    } catch (error) {
      console.error('Error loading case studies:',error);
      setCaseStudies([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfig=()=> {
    try {
      const adminConfig=getAdminConfig();
      if (adminConfig && adminConfig.caseStudies) {
        setConfig(adminConfig.caseStudies);
      }
    } catch (error) {
      console.error('Error loading config:',error);
    }
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    
    try {
      const studyData={
        ...formData,
        id: editingStudy?.id || Date.now().toString(),
        createdAt: editingStudy?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        results: formData.results.filter(result=> result.metric && result.value)
      };

      console.log('Saving case study:',studyData);
      const result=await saveCaseStudy(studyData);
      console.log('Save result:',result);
      
      // Reload the list after saving
      await loadCaseStudies();
      resetForm();
      
      // Show success message
      alert(editingStudy ? 'Case study updated successfully!' : 'Case study created successfully!');
    } catch (error) {
      console.error('Error saving case study:',error);
      alert('Failed to save case study. Please try again.');
    }
  };

  const resetForm=()=> {
    setFormData({
      title: '',
      client: '',
      industry: '',
      projectType: '',
      challenge: '',
      solution: '',
      results: [{metric: '',value: ''}],
      testimonial: '',
      author: '',
      image: '',
      gallery: [],
      projectUrl: '',
      demoUrl: '',
      technologies: [],
      duration: '',
      teamSize: '',
      featured: false,
      status: 'active'
    });
    setEditingStudy(null);
    setIsEditing(false);
  };

  const handleEdit=(study)=> {
    console.log('Editing case study:',study);
    setEditingStudy(study);
    setFormData({
      ...study,
      results: study.results || [{metric: '',value: ''}],
      gallery: study.gallery || [],
      technologies: study.technologies || []
    });
    setIsEditing(true);
  };

  const handleDelete=async (id)=> {
    console.log('Attempting to delete case study with id:',id);
    
    if (window.confirm('Are you sure you want to delete this case study? This action cannot be undone.')) {
      try {
        setIsDeleting(id); // Set loading state for this specific item
        
        console.log('Calling deleteCaseStudy...');
        const result=await deleteCaseStudy(id);
        console.log('Delete result:',result);
        
        if (result.success) {
          console.log('Delete successful, reloading case studies...');
          // Reload the list after successful deletion
          await loadCaseStudies();
          alert('Case study deleted successfully!');
        } else {
          throw new Error(result.error || 'Delete failed');
        }
      } catch (error) {
        console.error('Error deleting case study:',error);
        alert('Failed to delete case study. Please try again.');
      } finally {
        setIsDeleting(null); // Clear loading state
      }
    }
  };

  const handleChange=(e)=> {
    const {name,value,type,checked}=e.target;
    setFormData({
      ...formData,
      [name]: type==='checkbox' ? checked : value
    });
  };

  const handleResultChange=(index,field,value)=> {
    const newResults=[...formData.results];
    newResults[index]={...newResults[index],[field]: value};
    setFormData({
      ...formData,
      results: newResults
    });
  };

  const addResult=()=> {
    setFormData({
      ...formData,
      results: [...formData.results,{metric: '',value: ''}]
    });
  };

  const removeResult=(index)=> {
    setFormData({
      ...formData,
      results: formData.results.filter((_,i)=> i !==index)
    });
  };

  const handleImageUpload=(imageUrl)=> {
    setFormData(prev=> ({
      ...prev,
      image: imageUrl
    }));
  };

  const handleGalleryUpload=(imageUrl)=> {
    setFormData(prev=> ({
      ...prev,
      gallery: [...prev.gallery,imageUrl]
    }));
  };

  const removeFromGallery=(index)=> {
    setFormData(prev=> ({
      ...prev,
      gallery: prev.gallery.filter((_,i)=> i !==index)
    }));
  };

  const handleTechnologyToggle=(tech)=> {
    const technologies=formData.technologies.includes(tech)
      ? formData.technologies.filter(t=> t !==tech)
      : [...formData.technologies,tech];
    setFormData({
      ...formData,
      technologies
    });
  };

  const saveConfig=()=> {
    try {
      const adminConfig=getAdminConfig();
      adminConfig.caseStudies=config;
      saveAdminConfig(adminConfig);
      setShowConfig(false);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:',error);
      alert('Failed to save configuration. Please try again.');
    }
  };

  const addConfigItem=(type,item)=> {
    if (item && !config[type].includes(item)) {
      setConfig(prev=> ({
        ...prev,
        [type]: [...prev[type],item]
      }));
    }
  };

  const removeConfigItem=(type,index)=> {
    setConfig(prev=> ({
      ...prev,
      [type]: prev[type].filter((_,i)=> i !==index)
    }));
  };

  const filteredStudies=caseStudies.filter(study=> {
    const matchesSearch=study.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       study.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       study.challenge?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry=filterIndustry==='all' || study.industry===filterIndustry;
    return matchesSearch && matchesIndustry;
  });

  // Light Configuration View
  if (showConfig) {
    return (
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Case Study Configuration</h2>
          <motion.button
            onClick={()=> setShowConfig(false)}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
            <span>Back</span>
          </motion.button>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8 space-y-8 shadow-sm">
          {/* Industries */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiTag} className="h-5 w-5 text-blue-600" />
              <span>Industries</span>
            </h3>
            <div className="space-y-4">
              {config.industries.map((industry,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <span className="text-gray-900 font-medium">{industry}</span>
                  <motion.button
                    onClick={()=> removeConfigItem('industries',index)}
                    whileHover={{scale: 1.1}}
                    whileTap={{scale: 0.9}}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-300"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </motion.div>
              ))}
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Add new industry"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  onKeyPress={(e)=> {
                    if (e.key==='Enter') {
                      addConfigItem('industries',e.target.value);
                      e.target.value='';
                    }
                  }}
                />
                <motion.button
                  onClick={(e)=> {
                    const input=e.target.previousElementSibling;
                    addConfigItem('industries',input.value);
                    input.value='';
                  }}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Add
                </motion.button>
              </div>
            </div>
          </div>

          {/* Project Types */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiSettings} className="h-5 w-5 text-green-600" />
              <span>Project Types</span>
            </h3>
            <div className="space-y-4">
              {config.projectTypes.map((type,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <span className="text-gray-900 font-medium">{type}</span>
                  <motion.button
                    onClick={()=> removeConfigItem('projectTypes',index)}
                    whileHover={{scale: 1.1}}
                    whileTap={{scale: 0.9}}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-300"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </motion.div>
              ))}
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Add new project type"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                  onKeyPress={(e)=> {
                    if (e.key==='Enter') {
                      addConfigItem('projectTypes',e.target.value);
                      e.target.value='';
                    }
                  }}
                />
                <motion.button
                  onClick={(e)=> {
                    const input=e.target.previousElementSibling;
                    addConfigItem('projectTypes',input.value);
                    input.value='';
                  }}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Add
                </motion.button>
              </div>
            </div>
          </div>

          {/* Technologies */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
              <SafeIcon icon={FiSettings} className="h-5 w-5 text-purple-600" />
              <span>Technologies</span>
            </h3>
            <div className="space-y-4">
              {config.technologies.map((tech,index)=> (
                <motion.div
                  key={index}
                  initial={{opacity: 0,x: -20}}
                  animate={{opacity: 1,x: 0}}
                  transition={{delay: index * 0.1}}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <span className="text-gray-900 font-medium">{tech}</span>
                  <motion.button
                    onClick={()=> removeConfigItem('technologies',index)}
                    whileHover={{scale: 1.1}}
                    whileTap={{scale: 0.9}}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-all duration-300"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </motion.button>
                </motion.div>
              ))}
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="Add new technology"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                  onKeyPress={(e)=> {
                    if (e.key==='Enter') {
                      addConfigItem('technologies',e.target.value);
                      e.target.value='';
                    }
                  }}
                />
                <motion.button
                  onClick={(e)=> {
                    const input=e.target.previousElementSibling;
                    addConfigItem('technologies',input.value);
                    input.value='';
                  }}
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-all duration-300"
                >
                  Add
                </motion.button>
              </div>
            </div>
          </div>

          <motion.button
            onClick={saveConfig}
            whileHover={{scale: 1.02}}
            whileTap={{scale: 0.98}}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300"
          >
            Save Configuration
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // Light Editing Form View
  if (isEditing) {
    return (
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="space-y-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {editingStudy ? 'Edit Case Study' : 'New Case Study'}
          </h2>
          <motion.button
            onClick={resetForm}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
            <span>Cancel</span>
          </motion.button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 space-y-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Title *</label>
                  <input
                    type="text"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter project title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Name *</label>
                  <input
                    type="text"
                    name="client"
                    required
                    value={formData.client}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Client name"
                  />
                </div>
              </div>

              {/* Challenge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Challenge *</label>
                <textarea
                  name="challenge"
                  required
                  rows={4}
                  value={formData.challenge}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Describe the challenge or problem"
                />
              </div>

              {/* Solution */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Our Solution *</label>
                <textarea
                  name="solution"
                  required
                  rows={4}
                  value={formData.solution}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Describe the solution implemented"
                />
              </div>

              {/* Results */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">Key Results</label>
                  <motion.button
                    type="button"
                    onClick={addResult}
                    whileHover={{scale: 1.05}}
                    whileTap={{scale: 0.95}}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                  >
                    <SafeIcon icon={FiPlus} className="h-4 w-4" />
                    <span>Add Result</span>
                  </motion.button>
                </div>
                <div className="space-y-3">
                  {formData.results.map((result,index)=> (
                    <div key={index} className="flex space-x-3">
                      <input
                        type="text"
                        placeholder="Metric (e.g.,Development time reduced)"
                        value={result.metric}
                        onChange={(e)=> handleResultChange(index,'metric',e.target.value)}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      />
                      <input
                        type="text"
                        placeholder="Value (e.g.,65%)"
                        value={result.value}
                        onChange={(e)=> handleResultChange(index,'value',e.target.value)}
                        className="w-24 px-3 py-2 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      />
                      {formData.results.length > 1 && (
                        <motion.button
                          type="button"
                          onClick={()=> removeResult(index)}
                          whileHover={{scale: 1.1}}
                          whileTap={{scale: 0.9}}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-all duration-300"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </motion.button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Testimonial</label>
                <textarea
                  name="testimonial"
                  rows={3}
                  value={formData.testimonial}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Client testimonial quote"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Testimonial Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="Name,Title at Company"
                />
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image</label>
                <ImageUploader
                  onUpload={handleImageUpload}
                  currentImage={formData.image}
                  label="Upload featured image"
                />
              </div>

              {/* Gallery */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Gallery</label>
                <ImageUploader
                  onUpload={handleGalleryUpload}
                  label="Add to gallery"
                  multiple
                />
                {formData.gallery.length > 0 && (
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    {formData.gallery.map((image,index)=> (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-20 object-cover rounded-xl border border-gray-200"
                        />
                        <motion.button
                          type="button"
                          onClick={()=> removeFromGallery(index)}
                          whileHover={{scale: 1.1}}
                          whileTap={{scale: 0.9}}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <SafeIcon icon={FiX} className="h-3 w-3" />
                        </motion.button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Industry & Type */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select industry</option>
                  {config.industries.map(industry=> (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Project Type</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select project type</option>
                  {config.projectTypes.map(type=> (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Project Details */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Duration</label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="e.g.,3 months"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Team Size</label>
                  <input
                    type="text"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="e.g.,4 developers"
                  />
                </div>
              </div>

              {/* URLs */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Project URL</label>
                  <input
                    type="url"
                    name="projectUrl"
                    value={formData.projectUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="https://project-url.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Demo URL</label>
                  <input
                    type="url"
                    name="demoUrl"
                    value={formData.demoUrl}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="https://demo-url.com"
                  />
                </div>
              </div>

              {/* Technologies */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Technologies Used</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {config.technologies.map(tech=> (
                    <label key={tech} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.technologies.includes(tech)}
                        onChange={()=> handleTechnologyToggle(tech)}
                        className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-2"
                      />
                      <span className="text-sm text-gray-700">{tech}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status & Featured */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="featured"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="featured" className="text-sm text-gray-700 flex items-center space-x-2">
                    <SafeIcon icon={FiStar} className="h-4 w-4 text-yellow-500" />
                    <span>Featured Case Study</span>
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <motion.button
                type="submit"
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiSave} className="h-5 w-5" />
                <span>{editingStudy ? 'Update Case Study' : 'Save Case Study'}</span>
              </motion.button>
            </div>
          </div>
        </form>
      </motion.div>
    );
  }

  // Main Case Study Manager View with Light Theme
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Case Study Manager</h2>
          <p className="text-gray-600">Manage your portfolio and case studies</p>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            onClick={()=> setShowConfig(true)}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-xl transition-all duration-300"
          >
            <SafeIcon icon={FiSettings} className="h-5 w-5" />
            <span>Configure</span>
          </motion.button>
          <motion.button
            onClick={()=> setIsEditing(true)}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="h-5 w-5" />
            <span>New Case Study</span>
          </motion.button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search case studies..."
                value={searchQuery}
                onChange={(e)=> setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <select
              value={filterIndustry}
              onChange={(e)=> setFilterIndustry(e.target.value)}
              className="px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
            >
              <option value="all">All Industries</option>
              {config.industries.map(industry=> (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2 bg-gray-100 rounded-xl p-1 border border-gray-200">
            <motion.button
              onClick={()=> setViewMode('grid')}
              whileTap={{scale: 0.95}}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode==='grid' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiGrid} className="h-4 w-4" />
            </motion.button>
            <motion.button
              onClick={()=> setViewMode('list')}
              whileTap={{scale: 0.95}}
              className={`p-2 rounded-lg transition-all duration-300 ${
                viewMode==='list' 
                  ? 'bg-blue-600 text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiList} className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Case Studies Display */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">All Case Studies ({filteredStudies.length})</h3>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading case studies...</p>
          </div>
        ) : filteredStudies.length > 0 ? (
          <div className={viewMode==='grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6' : 'divide-y divide-gray-100'}>
            {filteredStudies.map((study,index)=> (
              <motion.div
                key={study.id}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: index * 0.1}}
                className={viewMode==='grid' 
                  ? 'bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 group' 
                  : 'p-6 hover:bg-gray-50 transition-all duration-300'}
              >
                {viewMode==='grid' ? (
                  <>
                    {/* Grid Card View */}
                    <div className="relative h-48 bg-gray-100">
                      {study.image ? (
                        <img
                          src={study.image}
                          alt={study.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <SafeIcon icon={FiImage} className="h-12 w-12" />
                        </div>
                      )}

                      {/* Status Badge */}
                      <div className="absolute top-3 left-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          study.status==='active' 
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : study.status==='draft' 
                            ? 'bg-gray-100 text-gray-700 border-gray-200'
                            : 'bg-orange-100 text-orange-700 border-orange-200'
                        }`}>
                          {study.status}
                        </span>
                      </div>

                      {/* Featured Badge */}
                      {study.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200">
                            <SafeIcon icon={FiStar} className="h-3 w-3" />
                            <span>Featured</span>
                          </span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="absolute bottom-3 right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {study.projectUrl && (
                          <motion.a
                            href={study.projectUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{scale: 1.1}}
                            whileTap={{scale: 0.9}}
                            className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                            title="View Project"
                          >
                            <SafeIcon icon={FiExternalLink} className="h-4 w-4 text-gray-700" />
                          </motion.a>
                        )}
                        <motion.button
                          onClick={()=> handleEdit(study)}
                          whileHover={{scale: 1.1}}
                          whileTap={{scale: 0.9}}
                          className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors"
                          title="Edit"
                        >
                          <SafeIcon icon={FiEdit2} className="h-4 w-4 text-blue-600" />
                        </motion.button>
                        <motion.button
                          onClick={()=> handleDelete(study.id)}
                          disabled={isDeleting===study.id}
                          whileHover={{scale: 1.1}}
                          whileTap={{scale: 0.9}}
                          className="p-2 bg-white bg-opacity-90 rounded-lg hover:bg-opacity-100 transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {isDeleting===study.id ? (
                            <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <SafeIcon icon={FiTrash2} className="h-4 w-4 text-red-600" />
                          )}
                        </motion.button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-blue-600 font-medium">{study.industry}</span>
                        <span className="text-xs text-gray-500">{study.duration}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                        {study.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{study.client}</p>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {study.challenge}
                      </p>

                      {/* Key Results */}
                      {study.results && study.results.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {study.results.slice(0,2).map((result,index)=> (
                            <div key={index} className="text-center p-2 bg-gray-50 rounded-lg border border-gray-200">
                              <div className="text-sm font-bold text-blue-600">{result.value}</div>
                              <div className="text-xs text-gray-500 line-clamp-1">{result.metric}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Technologies */}
                      {study.technologies && study.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {study.technologies.slice(0,3).map((tech,index)=> (
                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full border border-blue-200">
                              {tech}
                            </span>
                          ))}
                          {study.technologies.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full border border-gray-200">
                              +{study.technologies.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                          {new Date(study.createdAt).toLocaleDateString()}
                        </span>
                        <div className="flex space-x-1">
                          {study.projectUrl && (
                            <SafeIcon icon={FiLink} className="h-4 w-4 text-gray-400" />
                          )}
                          {study.gallery && study.gallery.length > 0 && (
                            <SafeIcon icon={FiImage} className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* List View */
                  <div className="flex items-start justify-between">
                    <div className="flex space-x-4 flex-1">
                      {study.image && (
                        <img
                          src={study.image}
                          alt={study.title}
                          className="w-20 h-20 object-cover rounded-xl border border-gray-200"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{study.title}</h3>
                          {study.featured && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1 border border-yellow-200">
                              <SafeIcon icon={FiStar} className="h-3 w-3" />
                              <span>Featured</span>
                            </span>
                          )}
                          <span className={`text-xs font-medium px-2 py-1 rounded-full border ${
                            study.status==='active' 
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : study.status==='draft' 
                              ? 'bg-gray-100 text-gray-700 border-gray-200'
                              : 'bg-orange-100 text-orange-700 border-orange-200'
                          }`}>
                            {study.status}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{study.challenge}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUser} className="h-4 w-4" />
                            <span>{study.client}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiTag} className="h-4 w-4" />
                            <span>{study.industry}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiClock} className="h-4 w-4" />
                            <span>{study.duration}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <motion.button
                        onClick={()=> handleEdit(study)}
                        whileHover={{scale: 1.1}}
                        whileTap={{scale: 0.9}}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-300"
                        title="Edit"
                      >
                        <SafeIcon icon={FiEdit2} className="h-5 w-5" />
                      </motion.button>
                      <motion.button
                        onClick={()=> handleDelete(study.id)}
                        disabled={isDeleting===study.id}
                        whileHover={{scale: 1.1}}
                        whileTap={{scale: 0.9}}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300 disabled:opacity-50"
                        title="Delete"
                      >
                        {isDeleting===study.id ? (
                          <div className="h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                        )}
                      </motion.button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <SafeIcon icon={FiFolderOpen} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg mb-2">No case studies yet</p>
            <p className="text-gray-500 mb-6">Create your first case study to showcase your work</p>
            <motion.button
              onClick={()=> setIsEditing(true)}
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
            >
              Create First Case Study
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseStudyManager;